import React, { useState } from 'react';
import { BarChartIcon, TrendingUpIcon, ShoppingCartIcon, UsersIcon, PackageIcon, ArrowUpIcon, ArrowDownIcon, DollarSignIcon, CalendarIcon, InfoIcon } from 'lucide-react';
import { Shop } from '../pages/Shops';
interface ShopDashboardProps {
  shop: Shop;
}
export function ShopDashboard({
  shop
}: ShopDashboardProps) {
  const [hoveredRevenue, setHoveredRevenue] = useState<{
    month: string;
    revenue: number;
  } | null>(null);
  const [hoveredOrder, setHoveredOrder] = useState<{
    month: string;
    orders: number;
  } | null>(null);
  // Create more pronounced seasonal pattern for a beach shop
  // Higher sales in summer months, peak in July-August, lower in winter
  const seasonalPattern = [0.25, 0.3, 0.45, 0.6, 0.75, 0.9, 1.0, 0.95, 0.7, 0.5, 0.35, 0.4];
  // Generate revenue data with more pronounced seasonal patterns
  const revenueData = [{
    month: 'Jan',
    revenue: Math.round(shop.revenue.total * seasonalPattern[0])
  }, {
    month: 'Feb',
    revenue: Math.round(shop.revenue.total * seasonalPattern[1])
  }, {
    month: 'Mar',
    revenue: Math.round(shop.revenue.total * seasonalPattern[2])
  }, {
    month: 'Apr',
    revenue: Math.round(shop.revenue.total * seasonalPattern[3])
  }, {
    month: 'May',
    revenue: Math.round(shop.revenue.total * seasonalPattern[4])
  }, {
    month: 'Jun',
    revenue: Math.round(shop.revenue.total * seasonalPattern[5])
  }, {
    month: 'Jul',
    revenue: Math.round(shop.revenue.total * seasonalPattern[6])
  }, {
    month: 'Aug',
    revenue: Math.round(shop.revenue.total * seasonalPattern[7])
  }, {
    month: 'Sep',
    revenue: Math.round(shop.revenue.total * seasonalPattern[8])
  }, {
    month: 'Oct',
    revenue: Math.round(shop.revenue.total * seasonalPattern[9])
  }, {
    month: 'Nov',
    revenue: Math.round(shop.revenue.total * seasonalPattern[10])
  }, {
    month: 'Dec',
    revenue: Math.round(shop.revenue.total * seasonalPattern[11])
  }];
  // Generate orders data with the same seasonal pattern
  const ordersData = [{
    month: 'Jan',
    orders: Math.round(shop.ordersCount * seasonalPattern[0])
  }, {
    month: 'Feb',
    orders: Math.round(shop.ordersCount * seasonalPattern[1])
  }, {
    month: 'Mar',
    orders: Math.round(shop.ordersCount * seasonalPattern[2])
  }, {
    month: 'Apr',
    orders: Math.round(shop.ordersCount * seasonalPattern[3])
  }, {
    month: 'May',
    orders: Math.round(shop.ordersCount * seasonalPattern[4])
  }, {
    month: 'Jun',
    orders: Math.round(shop.ordersCount * seasonalPattern[5])
  }, {
    month: 'Jul',
    orders: Math.round(shop.ordersCount * seasonalPattern[6])
  }, {
    month: 'Aug',
    orders: Math.round(shop.ordersCount * seasonalPattern[7])
  }, {
    month: 'Sep',
    orders: Math.round(shop.ordersCount * seasonalPattern[8])
  }, {
    month: 'Oct',
    orders: Math.round(shop.ordersCount * seasonalPattern[9])
  }, {
    month: 'Nov',
    orders: Math.round(shop.ordersCount * seasonalPattern[10])
  }, {
    month: 'Dec',
    orders: Math.round(shop.ordersCount * seasonalPattern[11])
  }];
  const topProducts = [{
    name: 'Beach Towel',
    sales: 38,
    revenue: 1139.62
  }, {
    name: 'Sunglasses',
    sales: 27,
    revenue: 1079.73
  }, {
    name: 'Swim Shorts',
    sales: 24,
    revenue: 839.76
  }, {
    name: 'Beach Sandals',
    sales: 22,
    revenue: 549.78
  }];
  const customerStats = {
    total: 287,
    new: 42,
    returning: 245,
    conversionRate: 3.8
  };
  // Calculate max values for chart scaling
  const maxRevenue = Math.max(...revenueData.map(item => item.revenue));
  const maxOrders = Math.max(...ordersData.map(item => item.orders));
  return <div className="space-y-6" data-id="element-4289">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-4290">
        <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center" data-id="element-4291">
          <BarChartIcon size={18} className="mr-2 text-blue-600" data-id="element-4292" />
          Shop Dashboard
        </h2>
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-4293">
          {/* Revenue KPI */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-4294">
            <div className="flex justify-between items-start" data-id="element-4295">
              <div data-id="element-4296">
                <p className="text-sm text-gray-500 mb-1" data-id="element-4297">Total Revenue</p>
                <div className="text-2xl font-bold text-gray-900" data-id="element-4298">THB</div>
                <div className="text-3xl font-bold text-gray-900" data-id="element-4299">
                  {shop.revenue.total.toLocaleString('en-US')}
                </div>
              </div>
              <div className="p-2 bg-blue-50 rounded-md" data-id="element-4300">
                <DollarSignIcon size={20} className="text-blue-600" data-id="element-4301" />
              </div>
            </div>
            <div className="mt-2 flex items-center" data-id="element-4302">
              <span className={`inline-flex items-center text-xs font-medium ${shop.revenue.growth >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-4303">
                {shop.revenue.growth >= 0 ? <ArrowUpIcon size={12} className="mr-1" data-id="element-4304" /> : <ArrowDownIcon size={12} className="mr-1" data-id="element-4305" />}
                {Math.abs(shop.revenue.growth)}%
              </span>
              <span className="text-xs text-gray-500 ml-1" data-id="element-4306">vs last month</span>
            </div>
          </div>
          {/* Orders KPI */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-4307">
            <div className="flex justify-between items-start" data-id="element-4308">
              <div data-id="element-4309">
                <p className="text-sm text-gray-500 mb-1" data-id="element-4310">Total Orders</p>
                <h3 className="text-2xl font-bold text-gray-800" data-id="element-4311">
                  {shop.ordersCount}
                </h3>
              </div>
              <div className="p-2 bg-purple-50 rounded-md" data-id="element-4312">
                <ShoppingCartIcon size={20} className="text-purple-600" data-id="element-4313" />
              </div>
            </div>
            <div className="mt-2 flex items-center" data-id="element-4314">
              <span className="inline-flex items-center text-xs font-medium text-green-600" data-id="element-4315">
                <ArrowUpIcon size={12} className="mr-1" data-id="element-4316" />
                12.4%
              </span>
              <span className="text-xs text-gray-500 ml-1" data-id="element-4317">vs last month</span>
            </div>
          </div>
          {/* Products KPI */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-4318">
            <div className="flex justify-between items-start" data-id="element-4319">
              <div data-id="element-4320">
                <p className="text-sm text-gray-500 mb-1" data-id="element-4321">Total Products</p>
                <h3 className="text-2xl font-bold text-gray-800" data-id="element-4322">
                  {shop.productsCount}
                </h3>
              </div>
              <div className="p-2 bg-green-50 rounded-md" data-id="element-4323">
                <PackageIcon size={20} className="text-green-600" data-id="element-4324" />
              </div>
            </div>
            <div className="mt-2 flex items-center" data-id="element-4325">
              <span className="inline-flex items-center text-xs font-medium text-green-600" data-id="element-4326">
                <ArrowUpIcon size={12} className="mr-1" data-id="element-4327" />
                5.2%
              </span>
              <span className="text-xs text-gray-500 ml-1" data-id="element-4328">vs last month</span>
            </div>
          </div>
          {/* Customers KPI */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-4329">
            <div className="flex justify-between items-start" data-id="element-4330">
              <div data-id="element-4331">
                <p className="text-sm text-gray-500 mb-1" data-id="element-4332">Total Customers</p>
                <h3 className="text-2xl font-bold text-gray-800" data-id="element-4333">
                  {customerStats.total}
                </h3>
              </div>
              <div className="p-2 bg-orange-50 rounded-md" data-id="element-4334">
                <UsersIcon size={20} className="text-orange-600" data-id="element-4335" />
              </div>
            </div>
            <div className="mt-2 flex items-center" data-id="element-4336">
              <span className="inline-flex items-center text-xs font-medium text-green-600" data-id="element-4337">
                <ArrowUpIcon size={12} className="mr-1" data-id="element-4338" />
                8.7%
              </span>
              <span className="text-xs text-gray-500 ml-1" data-id="element-4339">vs last month</span>
            </div>
          </div>
        </div>
        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-4340">
          {/* Revenue Chart - Completely revamped implementation */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-4341">
            <div className="flex justify-between items-center mb-4" data-id="element-4342">
              <h4 className="text-sm font-medium text-gray-700" data-id="element-4343">
                Revenue Trend
              </h4>
              <div className="flex items-center text-xs text-gray-500" data-id="element-4344">
                <CalendarIcon size={14} className="mr-1" data-id="element-4345" />
                Last 12 months
              </div>
            </div>
            <div className="relative h-60 mb-6" data-id="element-4346">
              {/* Tooltip */}
              {hoveredRevenue && <div className="absolute top-0 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 z-10" data-id="element-4347">
                  {hoveredRevenue.month}: THB{' '}
                  {hoveredRevenue.revenue.toLocaleString('en-US')}
                </div>}
              {/* Y-axis labels */}
              <div className="absolute top-0 left-0 h-full flex flex-col justify-between text-xs text-gray-500 pr-2" data-id="element-4348">
                <div data-id="element-4349">THB {maxRevenue.toLocaleString('en-US')}</div>
                <div data-id="element-4350">
                  THB {Math.round(maxRevenue * 0.75).toLocaleString('en-US')}
                </div>
                <div data-id="element-4351">
                  THB {Math.round(maxRevenue * 0.5).toLocaleString('en-US')}
                </div>
                <div data-id="element-4352">
                  THB {Math.round(maxRevenue * 0.25).toLocaleString('en-US')}
                </div>
                <div data-id="element-4353">THB 0</div>
              </div>
              {/* Chart area */}
              <div className="absolute left-12 right-0 h-full flex items-end" data-id="element-4354">
                {/* Horizontal grid lines */}
                <div className="absolute inset-0" data-id="element-4355">
                  <div className="border-t border-gray-100 h-1/4" data-id="element-4356"></div>
                  <div className="border-t border-gray-100 h-1/4" data-id="element-4357"></div>
                  <div className="border-t border-gray-100 h-1/4" data-id="element-4358"></div>
                  <div className="border-t border-gray-100 h-1/4" data-id="element-4359"></div>
                </div>
                {/* Chart bars */}
                <div className="relative w-full h-full flex items-end" data-id="element-4360">
                  {revenueData.map((item, index) => {
                  const barHeight = `${item.revenue / maxRevenue * 100}%`;
                  return <div key={index} className="flex-1 flex flex-col items-center justify-end h-full" onMouseEnter={() => setHoveredRevenue(item)} onMouseLeave={() => setHoveredRevenue(null)} data-id="element-4361">
                        <div style={{
                      height: barHeight
                    }} className={`w-4/5 ${index === 11 ? 'bg-blue-600' : 'bg-blue-500 hover:bg-blue-600'} rounded-t transition-all duration-200`} data-id="element-4362"></div>
                      </div>;
                })}
                </div>
              </div>
              {/* X-axis labels */}
              <div className="absolute bottom-0 left-12 right-0 flex justify-between text-xs text-gray-500 pt-2" data-id="element-4363">
                {revenueData.map((item, index) => <div key={index} className="text-center" data-id="element-4364">
                    {item.month}
                  </div>)}
              </div>
            </div>
            <div className="text-xs text-gray-500 text-center" data-id="element-4365">
              <span className="inline-flex items-center" data-id="element-4366">
                <InfoIcon size={12} className="mr-1" data-id="element-4367" />
                Beach shop revenue shows seasonal patterns with summer peak
              </span>
            </div>
          </div>
          {/* Orders Chart - Completely revamped implementation */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-4368">
            <div className="flex justify-between items-center mb-4" data-id="element-4369">
              <h4 className="text-sm font-medium text-gray-700" data-id="element-4370">
                Orders Trend
              </h4>
              <div className="flex items-center text-xs text-gray-500" data-id="element-4371">
                <CalendarIcon size={14} className="mr-1" data-id="element-4372" />
                Last 12 months
              </div>
            </div>
            <div className="relative h-60 mb-6" data-id="element-4373">
              {/* Tooltip */}
              {hoveredOrder && <div className="absolute top-0 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 z-10" data-id="element-4374">
                  {hoveredOrder.month}: {hoveredOrder.orders.toLocaleString()}{' '}
                  orders
                </div>}
              {/* Y-axis labels */}
              <div className="absolute top-0 left-0 h-full flex flex-col justify-between text-xs text-gray-500 pr-2" data-id="element-4375">
                <div data-id="element-4376">{maxOrders.toLocaleString()}</div>
                <div data-id="element-4377">{Math.round(maxOrders * 0.75).toLocaleString()}</div>
                <div data-id="element-4378">{Math.round(maxOrders * 0.5).toLocaleString()}</div>
                <div data-id="element-4379">{Math.round(maxOrders * 0.25).toLocaleString()}</div>
                <div data-id="element-4380">0</div>
              </div>
              {/* Chart area */}
              <div className="absolute left-12 right-0 h-full flex items-end" data-id="element-4381">
                {/* Horizontal grid lines */}
                <div className="absolute inset-0" data-id="element-4382">
                  <div className="border-t border-gray-100 h-1/4" data-id="element-4383"></div>
                  <div className="border-t border-gray-100 h-1/4" data-id="element-4384"></div>
                  <div className="border-t border-gray-100 h-1/4" data-id="element-4385"></div>
                  <div className="border-t border-gray-100 h-1/4" data-id="element-4386"></div>
                </div>
                {/* Chart bars */}
                <div className="relative w-full h-full flex items-end" data-id="element-4387">
                  {ordersData.map((item, index) => {
                  const barHeight = `${item.orders / maxOrders * 100}%`;
                  return <div key={index} className="flex-1 flex flex-col items-center justify-end h-full" onMouseEnter={() => setHoveredOrder(item)} onMouseLeave={() => setHoveredOrder(null)} data-id="element-4388">
                        <div style={{
                      height: barHeight
                    }} className={`w-4/5 ${index === 11 ? 'bg-purple-600' : 'bg-purple-500 hover:bg-purple-600'} rounded-t transition-all duration-200`} data-id="element-4389"></div>
                      </div>;
                })}
                </div>
              </div>
              {/* X-axis labels */}
              <div className="absolute bottom-0 left-12 right-0 flex justify-between text-xs text-gray-500 pt-2" data-id="element-4390">
                {ordersData.map((item, index) => <div key={index} className="text-center" data-id="element-4391">
                    {item.month}
                  </div>)}
              </div>
            </div>
            <div className="text-xs text-gray-500 text-center" data-id="element-4392">
              <span className="inline-flex items-center" data-id="element-4393">
                <InfoIcon size={12} className="mr-1" data-id="element-4394" />
                Order volume follows similar seasonal trends as revenue
              </span>
            </div>
          </div>
        </div>
        {/* Additional Insights Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-4395">
          {/* Top Products */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-4396">
            <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-4397">
              Top Selling Products
            </h4>
            <div className="space-y-3" data-id="element-4398">
              {topProducts.map((product, index) => <div key={index} className="flex items-center justify-between border-b border-gray-100 pb-3 last:border-0 last:pb-0" data-id="element-4399">
                  <div className="flex items-center" data-id="element-4400">
                    <div className="w-8 h-8 bg-gray-100 rounded-md flex items-center justify-center text-gray-500 mr-3" data-id="element-4401">
                      {index + 1}
                    </div>
                    <div data-id="element-4402">
                      <p className="text-sm font-medium text-gray-800" data-id="element-4403">
                        {product.name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-4404">
                        {product.sales} sold
                      </p>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-4405">
                    <p className="text-sm font-medium text-gray-800" data-id="element-4406">
                      LKR{' '}
                      {product.revenue.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-4407">Revenue</p>
                  </div>
                </div>)}
            </div>
          </div>
          {/* Customer Insights */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-4408">
            <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-4409">
              Customer Insights
            </h4>
            <div className="grid grid-cols-2 gap-4" data-id="element-4410">
              <div className="bg-gray-50 rounded-md p-3" data-id="element-4411">
                <p className="text-xs text-gray-500 mb-1" data-id="element-4412">New Customers</p>
                <p className="text-lg font-medium text-gray-800" data-id="element-4413">
                  {customerStats.new}
                </p>
                <div className="mt-1 flex items-center" data-id="element-4414">
                  <span className="text-xs text-green-600 flex items-center" data-id="element-4415">
                    <ArrowUpIcon size={10} className="mr-1" data-id="element-4416" />
                    14.3%
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 rounded-md p-3" data-id="element-4417">
                <p className="text-xs text-gray-500 mb-1" data-id="element-4418">
                  Returning Customers
                </p>
                <p className="text-lg font-medium text-gray-800" data-id="element-4419">
                  {customerStats.returning}
                </p>
                <div className="mt-1 flex items-center" data-id="element-4420">
                  <span className="text-xs text-green-600 flex items-center" data-id="element-4421">
                    <ArrowUpIcon size={10} className="mr-1" data-id="element-4422" />
                    7.2%
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 rounded-md p-3" data-id="element-4423">
                <p className="text-xs text-gray-500 mb-1" data-id="element-4424">Avg. Order Value</p>
                <p className="text-lg font-medium text-gray-800" data-id="element-4425">
                  LKR{' '}
                  {(shop.revenue.total / shop.ordersCount).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                </p>
                <div className="mt-1 flex items-center" data-id="element-4426">
                  <span className="text-xs text-green-600 flex items-center" data-id="element-4427">
                    <ArrowUpIcon size={10} className="mr-1" data-id="element-4428" />
                    3.5%
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 rounded-md p-3" data-id="element-4429">
                <p className="text-xs text-gray-500 mb-1" data-id="element-4430">Conversion Rate</p>
                <p className="text-lg font-medium text-gray-800" data-id="element-4431">
                  {customerStats.conversionRate}%
                </p>
                <div className="mt-1 flex items-center" data-id="element-4432">
                  <span className="text-xs text-green-600 flex items-center" data-id="element-4433">
                    <ArrowUpIcon size={10} className="mr-1" data-id="element-4434" />
                    1.2%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}